import { ReactElement, useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { dashboardService } from 'services';
import { useAuth } from 'hooks';
import {
  DashboardCard,
  DashboardCardType,
  HomeFilterParam,
  QueryParam,
} from 'types';
import { Heading, Loading, ModalContent, useDrawer } from 'components';
import { getFinalDate, getInitialDate, swrKeys } from 'helpers';
import {
  CARD_INFO_DISPLAY_MAP,
  DASHBOARD_SUMMARY_DAYS_DIFFERENCE,
  FontWeight,
  ONE_MINUTE_MILLISECONDS,
} from 'commons';
import { BatchCollectCard, DeliveryFailReport, FilterCard } from './components';
import * as S from './styles';

const FIVE_MINUTES_IN_MILLISECONDS = 5 * ONE_MINUTE_MILLISECONDS;

export function SummaryCards() {
  const navigate = useNavigate();
  const { selectedStore } = useAuth();
  const { setIsOpen } = useDrawer();
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const store = selectedStore.name || undefined;
  const brand = selectedStore.brand || undefined;

  const startDate = getInitialDate({
    timeDifference: DASHBOARD_SUMMARY_DAYS_DIFFERENCE,
  });
  const endDate = getFinalDate();

  const { data, isLoading, isValidating, mutate } = useSWR(
    swrKeys.dashboardSummary({ brand, store }),
    () => dashboardService.getDashboardSummary({ store, startDate, endDate }),
    { refreshInterval: FIVE_MINUTES_IN_MILLISECONDS, revalidateOnFocus: false }
  );

  function clearUrlSearchParamFilter() {
    searchParams.delete(QueryParam.filter);
    setSearchParams(searchParams);
  }

  useEffect(() => {
    const { search } = location;

    const params = new URLSearchParams(search);
    const filter = params.get(QueryParam.filter);

    if (filter === HomeFilterParam.verifyConsignment) {
      setShowVerifyModal(true);
    }
  }, [location]);

  if (isLoading) return <Loading />;

  if (!data) return null;

  const { entries, lastBatchCollect } = data;

  const hasEntries = Boolean(entries.length);
  const hasLastBatchCollect = Boolean(lastBatchCollect.consignments.length);

  function handleAction(path: string) {
    setIsOpen(false);
    navigate(path);
  }

  if (!hasEntries && !hasLastBatchCollect) return null;

  const cardsGroups = entries.reduce(
    (
      acc: { importantActions: ReactElement[]; followUp: ReactElement[] },
      cur: DashboardCard
    ) => {
      const { alert, quantity, type } = cur;

      if (quantity <= 0) {
        return acc;
      }

      const newAcc = acc;
      const cardInfoDisplay = CARD_INFO_DISPLAY_MAP[type];

      if (!cardInfoDisplay) return newAcc;

      const isImportantActions = ![
        DashboardCardType.DeliveryFailure,
        DashboardCardType.OnCarriage,
      ].includes(type);

      if (isImportantActions) {
        newAcc.importantActions.push(
          <FilterCard
            key={type}
            cardInfoDisplay={cardInfoDisplay}
            alert={alert}
            quantity={quantity}
            type={type}
            action={() => handleAction(cardInfoDisplay.navigatePath)}
          />
        );
      } else {
        newAcc.followUp.push(
          <FilterCard
            key={type}
            cardInfoDisplay={cardInfoDisplay}
            alert={alert}
            quantity={quantity}
            type={type}
            action={() => handleAction(cardInfoDisplay.navigatePath)}
          />
        );
      }

      return newAcc;
    },
    { importantActions: [], followUp: [] }
  );

  return (
    <S.Wrapper className="large">
      {showVerifyModal && (
        <ModalContent
          handleClose={() => {
            setShowVerifyModal(false);
            clearUrlSearchParamFilter();
            mutate();
          }}
        >
          <DeliveryFailReport />
        </ModalContent>
      )}

      {Boolean(cardsGroups?.importantActions.length) && (
        <S.Wrapper>
          <S.CardTitle $isLoading={isValidating}>
            <Heading weight={FontWeight.bold}>Ações importantes</Heading>

            <div className="loader">
              <Loading small />
            </div>
          </S.CardTitle>

          <S.CardsWrapper>{cardsGroups?.importantActions}</S.CardsWrapper>
        </S.Wrapper>
      )}

      {Boolean(cardsGroups?.followUp.length || hasLastBatchCollect) && (
        <S.Wrapper>
          <S.CardTitle $isLoading={isValidating}>
            <Heading weight={FontWeight.bold}>Acompanhamento</Heading>

            <div className="loader">
              <Loading small />
            </div>
          </S.CardTitle>

          <S.CardsWrapper>
            {cardsGroups?.followUp}
            {hasLastBatchCollect && (
              <BatchCollectCard lastBatchCollect={lastBatchCollect} />
            )}
          </S.CardsWrapper>
        </S.Wrapper>
      )}
    </S.Wrapper>
  );
}
